import {
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  LinearProgress,
  Typography,
} from "@mui/material";
import { OrganizationUserContext } from "contexts/Organization";
import { SubscriptionContext, SubscriptionTab } from "contexts/Subscription";
import { useContext } from "react";
import { SubscriptionType } from "schemas/dashboard";
import { Plans } from "schemas/payments";

import { useSubscription } from "utils/useSubscription";

import PlansPrice from "../SharedComponents/Price";
import VATerms from "../SharedComponents/VATerms";
import {
  PlanAction,
  getCurrentPlanButton,
  getPlanLimitText,
  isCurrentPlan,
} from "../helpers";
import styles from "./styles";

interface Props {
  plan: Plans;
}

const STATS = [
  { value: "10x", label: "More Efficient" },
  { value: "24/7", label: "Always Working" },
  { value: "100%", label: "Hands-Free" },
];

const AutopilotPlanCard = ({ plan }: Props) => {
  const { currentOrg } = useContext(OrganizationUserContext);
  const { plans, emailSents, subscription, tab, bannerPromoCodes } =
    useContext(SubscriptionContext);
  const { onPlanButtonClick } = useSubscription();

  const planName = plans?.[plan]?.display_name;
  const action = getCurrentPlanButton(
    plan,
    subscription?.subscription?.planName || Plans.FREE,
    subscription,
  );
  const totalEmails = plans?.[plan]?.monthly_limit || 0;
  const BENEFITS = [
    {
      icon: "🚀",
      primary: (
        <>
          Send <strong>{totalEmails} emails</strong> per month
        </>
      ),
    },
    {
      icon: "🤖",
      primary: (
        <>
          Input your preferences for <strong>smart sourcing brands</strong> from
          sources like Instagram, TikTok, Google Maps & more
        </>
      ),
    },
    {
      icon: "✍️",
      primary: (
        <>
          <strong>Runs on autopilot</strong> so you set it up once and have
          weekly pitches sent automatically
        </>
      ),
    },
    {
      icon: "🎯",
      primary: (
        <>
          <strong>Full control</strong> to review, approve, or pause any time
        </>
      ),
    },
  ];

  const _isCurrentPlan =
    isCurrentPlan(subscription, plan) &&
    (subscription?.subscription?.status === SubscriptionType.ACTIVE ||
      subscription?.subscription?.status === SubscriptionType.PAST_DUE);

  return (
    <>
      <Box sx={styles.card}>
        <Box
          sx={[
            styles.planTitleCard,
            {
              background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)",
            },
          ]}
        >
          <Grid container justifyContent="flex-end">
            <Chip
              sx={{ backgroundColor: "purple.main", px: 1 }}
              label={
                <Typography variant="subtitle2" sx={{ fontSize: 13 }}>
                  <strong>
                    <Box component="span" className="fa-solid fa-crown" />{" "}
                    Hands-off pitching for busy creators{" "}
                  </strong>
                </Typography>
              }
            />
          </Grid>

          <Typography variant="h3" sx={{ mt: 6, color: "white" }}>
            {planName}{" "}
            {_isCurrentPlan && (
              <Box component="span" sx={{ color: "#f59e0b", fontSize: 16 }}>
                (Current Plan)
              </Box>
            )}
          </Typography>
          <Box sx={styles.price}>
            {tab === SubscriptionTab.monthly && (
              <PlansPrice plan={Plans.AUTOPILOT} />
            )}
            {tab === SubscriptionTab.yearly && (
              <PlansPrice plan={Plans.AUTOPILOT_YEARLY} />
            )}
          </Box>

          <Grid item xs={12}>
            <VATerms plan={plan} />
          </Grid>
          {_isCurrentPlan && (
            <Box sx={styles.rolloverBox}>
              <Box sx={{ ...styles.rolloverRow, mb: 0.5 }}>
                <Typography sx={styles.rolloverLabelDim}>
                  Email Credits Used
                </Typography>
                <Typography sx={styles.rolloverValue}>
                  {emailSents} /{" "}
                  {Math.max(emailSents, subscription?.monthlyLimit || 0)}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={Math.min(
                  100,
                  (emailSents /
                    Math.max(1, subscription?.monthlyLimit || emailSents)) *
                    100,
                )}
                sx={styles.rolloverProgress}
              />
              <Box sx={{ ...styles.rolloverRow, mb: 0.25 }}>
                <Typography sx={styles.rolloverLabel}>
                  Monthly Allotment
                </Typography>
                <Typography sx={styles.rolloverLabel}>
                  {(
                    (subscription?.monthlyLimit || 0) -
                    (subscription?.rolloverEmailCredits || 0)
                  ).toLocaleString()}
                </Typography>
              </Box>
              {(subscription?.rolloverEmailCredits || 0) > 0 && (
                <Box sx={styles.rolloverRow}>
                  <Typography sx={styles.rolloverLabel}>
                    Rolled over from last month
                  </Typography>
                  <Typography sx={styles.rolloverValueAccent}>
                    +{subscription!.rolloverEmailCredits!.toLocaleString()}
                  </Typography>
                </Box>
              )}
              <Divider sx={styles.rolloverDivider} />
              <Box sx={styles.rolloverRow}>
                <Typography sx={styles.rolloverTotal}>
                  Total available this month
                </Typography>
                <Typography sx={styles.rolloverTotal}>
                  {Math.max(
                    emailSents,
                    subscription?.monthlyLimit || 0,
                  ).toLocaleString()}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>

        <Box
          sx={{
            backgroundColor: "#fef9ec",
            border: "1px solid #fde68a",
            borderRadius: 2,
            px: 2,
            py: 1.5,
            mb: 2,
          }}
        >
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.25 }}
          >
            <Box
              component="i"
              className="fa-solid fa-arrow-trend-up"
              sx={{ fontSize: 13, color: "#d97706" }}
            />
            <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
              {_isCurrentPlan ? "Credit Rollover Active" : "Rollover Credits"}
            </Typography>
          </Box>
          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
            {_isCurrentPlan
              ? "Unused credits roll over each month (up to 250/month). You can build up a maximum of 1,000 total email credits per month."
              : "Unused emails roll over monthly"}
          </Typography>
        </Box>

        <Typography sx={{ mb: 2 }}>
          <strong>Everything in Pro Plan PLUS:</strong>
        </Typography>

        {BENEFITS.map((benefit, index) => (
          <Grid container key={index} alignItems="center" sx={{ mb: 1.5 }}>
            <Grid
              container
              justifyContent="left"
              alignItems="center"
              item
              xs="auto"
            >
              <Box sx={styles.iconBlock}>{benefit.icon}</Box>
            </Grid>
            <Grid item xs md>
              <Typography component="div">{benefit.primary}</Typography>
            </Grid>
          </Grid>
        ))}

        <Grid
          container
          justifyContent="space-around"
          sx={{
            backgroundColor: "#f8fafc",
            borderRadius: 2,
            py: 1.5,
            mb: 2,
          }}
        >
          {STATS.map((stat, index) => (
            <Grid item key={index} sx={{ textAlign: "center" }}>
              <Typography
                sx={{ fontWeight: 800, fontSize: 18, color: "#4338ca" }}
              >
                {stat.value}
              </Typography>
              <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
                {stat.label}
              </Typography>
            </Grid>
          ))}
        </Grid>

        {!currentOrg?.overridePlanName && (
          <Button
            disableElevation
            fullWidth
            variant={action === PlanAction.upgrade ? "contained" : "text"}
            sx={{
              mt: 1,
              py: 1,
              borderRadius: 2,
              backgroundColor:
                action === PlanAction.upgrade ? "#4338ca" : "#f4f8f6",
              "&:hover": {
                backgroundColor:
                  action === PlanAction.upgrade ? "#3730a3" : "#e8ece9",
              },
            }}
            onClick={() => onPlanButtonClick(plan, action)}
          >
            {action}{" "}
            {tab === SubscriptionTab.yearly &&
            (action === PlanAction.upgrade || action === PlanAction.downgrade)
              ? "+ Get Free Template"
              : ""}
          </Button>
        )}
      </Box>

      <Box sx={{ mt: 2 }}>
        {getPlanLimitText(
          subscription,
          plan,
          emailSents,
          plans,
          bannerPromoCodes,
        )}
      </Box>
    </>
  );
};

export default AutopilotPlanCard;
